// src/lib/validationScore.js

// Minimum score needed to allow "Validate"
export const VALIDATION_THRESHOLD = 70; // tweak later after real usage

// Weight per evidence type (rough, PRD-aligned assumptions)
const TYPE_WEIGHT = {
  interview: 10,
  usability: 10,
  ab_test: 14,
  analytics: 10,
  cohort: 8,
  survey: 6,
  support_ticket: 4,
  tech_spike: 6,
  feasibility_doc: 6,
  architecture: 4,
  pricing: 8,
  finance: 6,
  repo_link: 2,
  heatmap: 4,
  link: 2,
};

const clamp = (n, a, b) => Math.max(a, Math.min(b, n));

function daysSince(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const ms = Date.now() - d.getTime();
  return Math.max(0, Math.floor(ms / (1000 * 60 * 60 * 24)));
}

function recencyMultiplier(d) {
  if (d == null) return 1;
  if (d <= 14) return 1.2;   // fresh evidence
  if (d <= 30) return 1.1;   // somewhat fresh
  if (d >= 180) return 0.85; // stale
  return 1.0;
}

function strengthMultiplier(strength) {
  // Expect 1..5 (if missing, assume 3)
  const s = Number.isFinite(Number(strength)) ? Number(strength) : 3;
  // 1 -> 0.9, 3 -> 1.0, 5 -> 1.1
  return 0.8 + (clamp(s, 1, 5) * 0.06);
}

export function scoreEvidenceForProblem(allEvidence = [], problemId) {
  const pid = String(problemId);
  const items = (allEvidence || []).filter((e) => {
    const p =
      e?.problem_card_id ?? e?.problem_id ?? e?.problemId ?? e?.problem;
    return String(p) === pid;
  });

  let raw = 0;
  let lastDays = null;

  const has = {
    interview: false,
    usability: false,
    analytics: false,
    ab_test: false,
    pricing: false,
  };

  for (const e of items) {
    const type = String(e?.type || "link").toLowerCase();
    const w = TYPE_WEIGHT[type] ?? TYPE_WEIGHT.link;

    const d = daysSince(e?.observed_at || e?.dateISO || e?.created_at);
    if (d != null) lastDays = lastDays == null ? d : Math.min(lastDays, d);

    const mult = recencyMultiplier(d) * strengthMultiplier(e?.strength);
    raw += w * mult;

    if (type in has) has[type] = true;
  }

  // Soft cap before normalization
  const capped = Math.min(raw, 120);

  // Normalize to 0..100 with diminishing returns
  const score = clamp(Math.round((capped / 120) * 100), 0, 100);

  const suggestions = [];
  if (!has.interview) suggestions.push("Run 3+ user interviews");
  if (!has.usability) suggestions.push("Do 3+ usability tests");
  if (!has.analytics) suggestions.push("Add analytics or funnels");
  if (!has.ab_test) suggestions.push("Ship a small A/B test");
  if (!has.pricing) suggestions.push("Collect pricing/viability signal");
  if (lastDays != null && lastDays > 60) suggestions.push("Refresh stale evidence");

  return {
    score,
    lastEvidenceDays: lastDays,
    suggestions,
  };
}
